import React, { useState, useEffect } from 'react';
import {
  Form,
  Select,
  Switch,
  Button,
  Typography,
  Divider,
  message,
  Tabs,
  Input,
  Modal,
  InputNumber,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  BgColorsOutlined,
  BellOutlined,
  GlobalOutlined,
  LockOutlined,
  ShoppingOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons';
import useThemeStore from '../store/themeStore';
import useAuthStore from '../store/authStore';
import useSettingsStore from '../store/settingsStore';
import ProductsSettings from '../components/ProductsSettings';
import apiService from '../services/apiService';
import PageIcon from '../components/Layout/PageIcon';

const { Title, Text } = Typography;
const { Option } = Select;

const SettingsPage = () => {
  const { theme, setTheme } = useThemeStore();
  const { user, updateProfile, isLoading } = useAuthStore();
  const {
    currency: storedCurrency,
    fxRate: storedFxRate,
    timezone: storedTimezone,
    dateFormat: storedDateFormat,
    fetchSettings,
    updateSettings: persistTenantSettings,
  } = useSettingsStore();

  const [notifyForm] = Form.useForm();
  const [regionalForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const [fxRateModalVisible, setFxRateModalVisible] = useState(false);
  const [pendingRegionalValues, setPendingRegionalValues] = useState(null);
  const [fxRateInput, setFxRateInput] = useState(1);
  const [savingRegional, setSavingRegional] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Hydrate notifications form from user.preferences each time the user object changes
  useEffect(() => {
    const n = user?.preferences?.notifications || {};
    notifyForm.setFieldsValue({
      email: n.email ?? true,
      push: n.push ?? true,
      leads: n.leads ?? true,
      deals: n.deals ?? true,
      tasks: n.tasks ?? true,
      weekly: n.weekly ?? true,
    });
  }, [notifyForm, user?.preferences?.notifications]);

  useEffect(() => {
    regionalForm.setFieldsValue({
      currency: storedCurrency,
      fxRate: storedFxRate,
      timezone: storedTimezone,
      dateFormat: storedDateFormat,
      language: user?.preferences?.language || 'en',
    });
  }, [
    regionalForm,
    storedCurrency,
    storedFxRate,
    storedTimezone,
    storedDateFormat,
    user?.preferences?.language,
  ]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    message.success(`Theme changed to ${newTheme}`);
  };

  const handleNotificationsSubmit = async (values) => {
    setSavingNotifications(true);
    const result = await updateProfile({
      preferences: {
        ...user?.preferences,
        notifications: {
          email: !!values.email,
          push: !!values.push,
          leads: !!values.leads,
          deals: !!values.deals,
          tasks: !!values.tasks,
          weekly: !!values.weekly,
        },
      },
    });
    setSavingNotifications(false);
    if (result.success) {
      message.success('Notification preferences saved');
    } else {
      message.error(result.error || 'Failed to save preferences');
    }
  };

  const persistRegional = async ({ currency, fxRate, timezone, dateFormat, language }) => {
    setSavingRegional(true);
    const result = await persistTenantSettings({ currency, fxRate, timezone, dateFormat });
    if (language) {
      await updateProfile({ preferences: { ...user?.preferences, language } });
    }
    setSavingRegional(false);
    if (result.success) {
      message.success('Regional settings saved');
    } else {
      message.error(result.error || 'Failed to save regional settings');
    }
  };

  const handleRegionalSubmit = async (values) => {
    const targetCurrency = values.currency || 'INR';
    if (targetCurrency !== 'INR' && Number(values.fxRate) <= 1) {
      setPendingRegionalValues(values);
      setFxRateInput(storedFxRate > 1 ? storedFxRate : 1);
      setFxRateModalVisible(true);
      return;
    }
    const fxRate = targetCurrency === 'INR' ? 1 : Number(values.fxRate) || 1;
    await persistRegional({ ...values, fxRate });
  };

  const handleFxRateConfirm = async () => {
    const rate = Number(fxRateInput);
    if (!isFinite(rate) || rate <= 0) {
      message.error('Enter a valid positive rate');
      return;
    }
    setFxRateModalVisible(false);
    await persistRegional({ ...pendingRegionalValues, fxRate: rate });
    setPendingRegionalValues(null);
  };

  const handleChangePassword = async (values) => {
    setChangingPassword(true);
    try {
      await apiService.post('/auth/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('Password changed successfully');
      passwordForm.resetFields();
    } catch (err) {
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(apiMsg || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Title level={2} className="mb-2" style={{ display: 'flex', alignItems: 'center' }}>
          <PageIcon name="settings" /> Settings
        </Title>
        <Text className="text-gray-600 dark:text-gray-400">
          Customize your CRM experience and preferences
        </Text>
      </div>

      <Tabs
        defaultActiveKey="appearance"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-soft p-6"
        items={[
          {
            key: 'appearance',
            label: <span><BgColorsOutlined /> Appearance</span>,
            children: (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <Text className="block font-medium">Theme</Text>
                    <Text className="text-sm text-gray-500 dark:text-gray-400">
                      Choose your preferred color scheme
                    </Text>
                  </div>
                  <Select value={theme} onChange={handleThemeChange} style={{ width: 140 }}>
                    <Option value="light">Light</Option>
                    <Option value="dark">Dark</Option>
                    <Option value="system">System</Option>
                  </Select>
                </div>
              </div>
            ),
          },
          {
            key: 'notifications',
            label: <span><BellOutlined /> Notifications</span>,
            children: (
              <Form form={notifyForm} layout="vertical" onFinish={handleNotificationsSubmit}>
                <div className="space-y-4">
                  {[
                    { name: 'email', title: 'Email Notifications', desc: 'Receive notifications via email' },
                    { name: 'push', title: 'Push Notifications', desc: 'Receive browser push notifications' },
                    { name: 'leads', title: 'New Lead Alerts', desc: 'Get notified when new leads are created or assigned to you' },
                    { name: 'deals', title: 'Deal Updates', desc: 'Get notified about deal stage changes' },
                    { name: 'tasks', title: 'Task Reminders', desc: 'Receive reminders for upcoming tasks' },
                    { name: 'weekly', title: 'Weekly Reports', desc: 'Receive weekly performance summaries' },
                  ].map((row, idx, arr) => (
                    <React.Fragment key={row.name}>
                      <div className="flex justify-between items-center">
                        <div>
                          <Text className="block font-medium">{row.title}</Text>
                          <Text className="text-sm text-gray-500 dark:text-gray-400">{row.desc}</Text>
                        </div>
                        <Form.Item name={row.name} valuePropName="checked" className="mb-0">
                          <Switch />
                        </Form.Item>
                      </div>
                      {idx < arr.length - 1 && <Divider className="my-2" />}
                    </React.Fragment>
                  ))}
                </div>
                <Divider className="my-6" />
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={savingNotifications || isLoading}
                    icon={<SaveOutlined />}
                  >
                    Save Notification Settings
                  </Button>
                </Form.Item>
              </Form>
            ),
          },
          {
            key: 'regional',
            label: <span><GlobalOutlined /> Regional</span>,
            children: (
              <>
                <Form
                  form={regionalForm}
                  layout="vertical"
                  onFinish={handleRegionalSubmit}
                  initialValues={{
                    language: user?.preferences?.language || 'en',
                    timezone: storedTimezone,
                    dateFormat: storedDateFormat,
                    currency: storedCurrency,
                    fxRate: storedFxRate,
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item name="language" label="Language">
                      <Select>
                        <Option value="en">English</Option>
                        <Option value="es">Spanish</Option>
                        <Option value="fr">French</Option>
                        <Option value="de">German</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item name="timezone" label="Timezone">
                      <Select showSearch placeholder="Select timezone">
                        <Option value="UTC">UTC</Option>
                        <Option value="America/New_York">Eastern Time</Option>
                        <Option value="America/Chicago">Central Time</Option>
                        <Option value="America/Denver">Mountain Time</Option>
                        <Option value="America/Los_Angeles">Pacific Time</Option>
                        <Option value="Europe/London">London</Option>
                        <Option value="Europe/Paris">Paris</Option>
                        <Option value="Asia/Kolkata">India (IST)</Option>
                        <Option value="Asia/Tokyo">Tokyo</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item name="dateFormat" label="Date Format">
                      <Select>
                        <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                        <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                        <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="currency"
                      label="Display Currency"
                      tooltip="Deal/target values are stored in INR. This setting controls how amounts are displayed across the app."
                    >
                      <Select>
                        <Option value="INR">INR (₹)</Option>
                        <Option value="USD">USD ($)</Option>
                        <Option value="EUR">EUR (€)</Option>
                        <Option value="GBP">GBP (£)</Option>
                        <Option value="JPY">JPY (¥)</Option>
                        <Option value="CAD">CAD (C$)</Option>
                        <Option value="AUD">AUD (A$)</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item shouldUpdate={(prev, curr) => prev.currency !== curr.currency} noStyle>
                      {({ getFieldValue }) => {
                        const cur = getFieldValue('currency');
                        if (!cur || cur === 'INR') return null;
                        return (
                          <Form.Item
                            name="fxRate"
                            label={`Conversion Rate (1 ${cur} = ? INR)`}
                            rules={[
                              { required: true, message: 'Enter the conversion rate' },
                              { type: 'number', min: 0.0001, message: 'Rate must be positive' },
                            ]}
                          >
                            <InputNumber
                              min={0.0001}
                              step={0.01}
                              className="w-full"
                              placeholder={`e.g. 83.5 means 1 ${cur} = 83.5 INR`}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </div>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={savingRegional}
                    >
                      Save Regional Settings
                    </Button>
                  </Form.Item>
                </Form>
                <Modal
                  title="Set conversion rate"
                  open={fxRateModalVisible}
                  onOk={handleFxRateConfirm}
                  onCancel={() => {
                    setFxRateModalVisible(false);
                    setPendingRegionalValues(null);
                  }}
                  okText="Save"
                  destroyOnClose
                >
                  <p>
                    You're switching the display currency to{' '}
                    <strong>{pendingRegionalValues?.currency}</strong>. Enter how many INR equal 1{' '}
                    {pendingRegionalValues?.currency}.
                  </p>
                  <InputNumber
                    autoFocus
                    min={0.0001}
                    step={0.01}
                    value={fxRateInput}
                    onChange={setFxRateInput}
                    className="w-full"
                    placeholder={`e.g. 83.5 means 1 ${pendingRegionalValues?.currency || 'USD'} = 83.5 INR`}
                  />
                </Modal>
              </>
            ),
          },
          {
            key: 'products',
            label: <span><ShoppingOutlined /> Products</span>,
            children: <ProductsSettings />,
          },
          {
            key: 'security',
            label: <span><LockOutlined /> Security</span>,
            children: (
              <div className="space-y-6">
                <Alert
                  type="info"
                  showIcon
                  message="Tip"
                  description="Use a strong password (8+ chars, mix of upper/lower case, a digit, and a symbol). After changing your password, your other open sessions will continue to work until they expire."
                />
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handleChangePassword}
                  autoComplete="off"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                      name="currentPassword"
                      label="Current password"
                      rules={[{ required: true, message: 'Enter your current password' }]}
                    >
                      <Input.Password
                        autoComplete="current-password"
                        placeholder="••••••••"
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                      />
                    </Form.Item>

                    <div />

                    <Form.Item
                      name="newPassword"
                      label="New password"
                      rules={[
                        { required: true, message: 'Choose a new password' },
                        { min: 8, message: 'Minimum 8 characters' },
                        {
                          pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                          message: 'Must include uppercase, lowercase, digit and symbol',
                        },
                      ]}
                      hasFeedback
                    >
                      <Input.Password
                        autoComplete="new-password"
                        placeholder="At least 8 chars, mixed case, digit, symbol"
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      label="Confirm new password"
                      dependencies={['newPassword']}
                      hasFeedback
                      rules={[
                        { required: true, message: 'Confirm the new password' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('newPassword') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        autoComplete="new-password"
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                      />
                    </Form.Item>
                  </div>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<LockOutlined />}
                      loading={changingPassword}
                    >
                      Change Password
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SettingsPage;
